// 介錯と覚醒王の当たりの前端 = 描かれた絵の前端 + 余白 (2026-10-09 ユーザー採用 (検収パック V-1・V-2「絵に合わせる」) = 配布版の既定)。
//
// 方針 (ユーザー 10-05 原文):「他のボスでも共通で、基本は絵に合わせる。絵に合わないけど攻撃の種類的に射程を伸ばしたい、
//   絵だけでやろうとすると不自然な距離の移動になる場合はエフェクトでごまかす」= 当たりの前端 = 絵の前端 + 15px
//   (花嫁・亡霊は src/bride_shade_reach.ts で実施済み。ここは同じ方針)。
// 形: 値は attacks.ts へ直接書かず、絵の前端の採寸表 (下の *_ART_FRONT_PX) から導いて、この module を読んだ時に ATTACKS へ当てる
//   (= クエリなしで 'both')。attacks.ts の「旧値」がそのまま残るので `?reachArt=off` で旧へ戻せる。
//   採寸表が単一の正本で、絵を差し替えたら測り直してここだけ更新する (tests/combat_sim/exec_awoken_reach_fit.test.ts が見張る)。
// 採寸 = docs/research/2026-10-09-executioner-awoken-reach-measure.md §3 (実機の通常ループ・スプライト本体の不透明画素)。
// 旧へ戻す切り替え = DEV の `?reachArt=off` (他に executioner|awoken|both・src/main.ts の DEV の箱で読む・配布版へは漏らさない = scripts/check_dev_leak.mjs)。
//
// 介錯: range / approachDistancePx / strike.localToPx を絵＋15 へ。後ろ側 (localFromPx) は変えない。
//   approachDistancePx は「旧の値」と「新しい前端 (段のある技は一番短い段)」の小さい方
//   = 踏み込みの着地 (approachDistancePx − gapCloseBuffer) が必ず当たりの内側に入る (bride_shade_reach.ts と同じ考え)。
//   突き当てだけは当たりが伸びる向き (120→180)。着地は旧のまま (位置避け可の技で、前へ決まった量だけ出る)。
// 覚醒王: 噛み系 (口の位置から出す当たり) は変えない。絵の前足が当たりより前に出る段だけ伸ばす。
//   暴連噛の2段・伸びの2段 157→182 / 伸びの4段 157→186 (frenzy_bite2 の絵 167〜171 + 15)。
//   暴走突進・追い噛みの1発目 150→165 (後ろ -100 はそのまま)。暴噛・終 (致命) 141〜147→178 (前足の爪 163 + 15)。
//   懐払いは変えない (尾は FX 扱い = 2026-10-30 ユーザー決定・決裁箱の答え)。
//   噛みの段は口から毎tick 出す当たりなので、spawnMeleeHit が ActiveHit.mouthFrontToPx に前端を焼き、hits.ts が伸ばす。
import { ATTACKS } from './attacks'
import { finisherReachFrontPx } from './executioner_finisher_foot'
import type { AttackDef, StrikeProfile } from './types'

export type ExecAwokenReachVariant = 'off' | 'executioner' | 'awoken' | 'both'
export const EXEC_AWOKEN_REACH_VARIANTS: readonly ExecAwokenReachVariant[] = ['off', 'executioner', 'awoken', 'both']

export const REACH_ART_MARGIN_PX = 15

// 当たりが生きている間の絵の前端 (px・ボス中心から前方・測定文書 §1/§2 の「絵の前端（全体）」の大きい方)。
export const EXEC_ART_FRONT_PX = {
  exec_maulDrop: 93,
  exec_maulSet: [120, 74, 102],   // 段ごと (2段は頭上に掲げた刃を含む全体)
  exec_maulSetPierce: 144,
  exec_maulHold: 106,             // 振り抜き後の前足
  exec_maulQuake: 88,
  exec_maulJab: 165,
  exec_cannonSweep: 130,
  executioner_phaseTransition: 112,
  executioner_finisher: 100,
} as const
export type ExecReachId = keyof typeof EXEC_ART_FRONT_PX
export const EXEC_REACH_IDS = Object.keys(EXEC_ART_FRONT_PX) as ExecReachId[]

export const AWOKEN_ART_FRONT_PX = {
  frenzyBite2: 167,          // 暴連噛の2段・伸びの2段 (frenzy_bite2 の絵)
  frenzyExtendTrap: 171,     // 伸びの4段 (罠の1発)
  rush: 150,                 // 暴走突進・追い噛みの1発目 (前足)
  execBite: 163,             // 暴噛・終 (前足の爪)
} as const

// 致命の絵の前端を、致命の4枚の置き方 (src/executioner_finisher_foot.ts) の打点の絵の刃先へ替える。null = 上の表のまま。
//   既定 = mid の 116 (10-09 ユーザー採用・配布版)。旧の置き方 (DEV ?execFinisherFoot=old) で null = 表の 100。
let finisherFrontOverride: number | null = finisherReachFrontPx()
// 介錯の段ごとの新しい前端 (絵＋15)。段の無い技は1要素。
export function execArtFrontsPx(id: ExecReachId): number[] {
  const v = id === 'executioner_finisher' && finisherFrontOverride !== null ? finisherFrontOverride : EXEC_ART_FRONT_PX[id]
  return (typeof v === 'number' ? [v] : [...v]).map(f => f + REACH_ART_MARGIN_PX)
}

// 覚醒王: 口で当てる段の前端 (絵に合わせる時だけ値を返す)。spawnMeleeHit が呼ぶ。
const AWOKEN_MOUTH_FRONT: Readonly<Record<string, Readonly<Record<number, number>>>> = {
  feralFrenzy: { 1: AWOKEN_ART_FRONT_PX.frenzyBite2 + REACH_ART_MARGIN_PX },
  feralFrenzyExtend: { 1: AWOKEN_ART_FRONT_PX.frenzyBite2 + REACH_ART_MARGIN_PX, 3: AWOKEN_ART_FRONT_PX.frenzyExtendTrap + REACH_ART_MARGIN_PX },
  kingExecutionAwoken: { 0: AWOKEN_ART_FRONT_PX.execBite + REACH_ART_MARGIN_PX },
}
export const AWOKEN_RUSH_IDS = ['feralRush', 'feralRushChase'] as const
// 突進の後ろ側 = いまの控えの形 (hits.ts の collisionDraft: -min(range, BOSS.rearHitRange 100))。触らない。
const AWOKEN_RUSH_REAR_PX = -100

type Snap = { range: number; approach: number | undefined; strike: StrikeProfile | undefined; stages: AttackDef['comboStages'] }
let captured: Record<string, Snap> | null = null
function capture(): Record<string, Snap> {
  const out: Record<string, Snap> = {}
  for (const id of [...EXEC_REACH_IDS, ...AWOKEN_RUSH_IDS]) {
    const a = ATTACKS[id]!
    out[id] = { range: a.range, approach: a.approachDistancePx, strike: a.strike ? { ...a.strike } : undefined,
      stages: a.comboStages?.map(s => ({ ...s })) }
  }
  return out
}
function restore(id: string): void {
  const a = ATTACKS[id]!, s = captured![id]!
  a.range = s.range
  if (s.approach === undefined) delete a.approachDistancePx
  else a.approachDistancePx = s.approach
  if (s.strike === undefined) delete a.strike
  else a.strike = { ...s.strike }
  if (s.stages === undefined) delete a.comboStages
  else a.comboStages = s.stages.map(st => ({ ...st }))
}

function applyExecutioner(): void {
  for (const id of EXEC_REACH_IDS) {
    const a = ATTACKS[id]!, s = captured![id]!
    const fronts = execArtFrontsPx(id)
    const from = s.strike!.localFromPx
    a.range = Math.max(...fronts)
    a.approachDistancePx = Math.min(s.approach ?? s.range, Math.min(...fronts))
    a.strike = { localFromPx: from, localToPx: fronts[0]! }
    if (fronts.length > 1) {
      a.comboStages = Array.from({ length: a.comboHits ?? fronts.length }, (_, i) => ({
        ...(s.stages?.[i] ?? {}), strike: { localFromPx: from, localToPx: fronts[i] ?? fronts[0]! } }))
    }
  }
}
function applyAwoken(): void {
  for (const id of AWOKEN_RUSH_IDS) {
    // range (= collisionDraft の踏み込みの着地) は今のまま。着地 150-28=122 は新しい前端 165 の内側。
    ATTACKS[id]!.strike = { localFromPx: AWOKEN_RUSH_REAR_PX, localToPx: AWOKEN_ART_FRONT_PX.rush + REACH_ART_MARGIN_PX }
  }
}

let current: ExecAwokenReachVariant = 'off'
export function getExecAwokenReachVariant(): ExecAwokenReachVariant { return current }
export function execReachArtOn(): boolean { return current === 'executioner' || current === 'both' }
export function awokenReachArtOn(): boolean { return current === 'awoken' || current === 'both' }

// テストと DEV の起動時 (src/main.ts) から呼ぶ。'off' で attacks.ts に書いた旧の値へ戻す。
export function setExecAwokenReachVariant(variant: ExecAwokenReachVariant): void {
  if (variant === current) return
  if (!captured) captured = capture()
  for (const id of Object.keys(captured)) restore(id)
  current = variant
  if (execReachArtOn()) applyExecutioner()
  if (awokenReachArtOn()) applyAwoken()
}

/** 致命の絵の前端を比較版の値へ替える (null で表の値へ戻す)。当たりを絵に合わせている間は、その場で当たりも付け替える。 */
export function setFinisherArtFrontOverride(px: number | null): void {
  finisherFrontOverride = px
  if (!captured) return
  for (const id of Object.keys(captured)) restore(id)
  if (execReachArtOn()) applyExecutioner()
  if (awokenReachArtOn()) applyAwoken()
}

export function parseExecAwokenReachVariant(raw: string | null): ExecAwokenReachVariant | null {
  return raw !== null && (EXEC_AWOKEN_REACH_VARIANTS as readonly string[]).includes(raw) ? raw as ExecAwokenReachVariant : null
}

/** 覚醒王の口で当てる段の、絵に合わせた前端。'off'・対象外の段は undefined (= 旧の当たりのまま)。 */
export function awokenMouthArtFrontPx(attackId: string, comboHitIndex: number): number | undefined {
  if (!awokenReachArtOn()) return undefined
  return AWOKEN_MOUTH_FRONT[attackId]?.[comboHitIndex]
}

// 既定 = 絵に合わせる (2026-10-09 ユーザー採用)。旧へ戻すのは DEV の ?reachArt=off かテストの setExecAwokenReachVariant('off')。
setExecAwokenReachVariant('both')
